import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft2, Cup } from "iconsax-react";

import MobileLayout from "../../components/MobileLayout";
import { Md3Button } from "../../shared/components";
import { useGamification } from "../../hooks/useGamification";
import { shuffle, type GameWord } from "../../hooks/useGameWords";
import {
  CompleteCard,
  Feedback,
  GamePageRoot,
  GameTitle,
  GameTopBar,
  OptionBtn,
  OptionsGrid,
  ProgressText,
  ScoreBadge,
} from "./gameStyles";

type Props = { words: GameWord[]; onExit?: () => void };

function normalizeLetters(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase();
}

const DactylologyPuzzleGame: React.FC<Props> = ({ words, onExit }) => {
  const navigate = useNavigate();
  const { addPoints } = useGamification();

  const questions = useMemo(() => {
    return words
      .map((w) => {
        const letters = normalizeLetters(w.palavra);
        if (letters.length < 2 || letters.length > 8) return null;
        const scrambled = shuffle(letters.split("")).join("");
        if (scrambled === letters) return null;
        return { word: w, answer: letters, hint: scrambled };
      })
      .filter(Boolean) as { word: GameWord; answer: string; hint: string }[];
  }, [words]);

  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<string[]>([]);
  const [score, setScore] = useState(0);
  const [checked, setChecked] = useState(false);
  const [done, setDone] = useState(false);

  const current = questions[index];
  const built = picked.join("");
  const isCorrect = built === current?.answer;

  const letterButtons = useMemo(() => {
    if (!current) return [];
    const usedIndex: Record<string, number> = {};
    picked.forEach((ch) => {
      usedIndex[ch] = (usedIndex[ch] || 0) + 1;
    });
    const seen: Record<string, number> = {};
    return current.hint.split("").map((ch, i) => {
      seen[ch] = (seen[ch] || 0) + 1;
      const used = (usedIndex[ch] || 0) >= seen[ch];
      return { ch, key: `${i}-${ch}`, used };
    });
  }, [current, picked]);

  const handleCheck = async () => {
    if (!current || checked) return;
    setChecked(true);
    if (built === current.answer) {
      setScore((s) => s + 1);
      await addPoints("QUIZ_DATILOLOGIA");
    }
  };

  const handleNext = () => {
    if (index + 1 >= questions.length) {
      setDone(true);
      return;
    }
    setIndex((i) => i + 1);
    setPicked([]);
    setChecked(false);
  };

  return (
    <MobileLayout
      headerTitle="Quebra-cabeça Datilologia"
      headerType="private"
      showBannerAd={false}
    >
      <GamePageRoot>
        <GameTopBar>
          <Md3Button
            title="Voltar"
            variant="text"
            onClick={() => (onExit ? onExit() : navigate("/games"))}
            icon={<ArrowLeft2 size={18} />}
          />
          <ScoreBadge>
            <Cup size={16} variant="Bold" />
            {score}
          </ScoreBadge>
        </GameTopBar>

        {done || !current ? (
          <CompleteCard>
            <GameTitle>{done ? "Muito bem!" : "Sem palavras"}</GameTitle>
            {done ? (
              <ProgressText>
                Acertos: {score}/{questions.length}
              </ProgressText>
            ) : null}
            <Md3Button
              title="Voltar aos jogos"
              onClick={() => navigate("/games")}
            />
          </CompleteCard>
        ) : (
          <>
            <ProgressText>
              Monte a palavra {index + 1}/{questions.length}
            </ProgressText>
            <GameTitle style={{ letterSpacing: 6, fontSize: 28 }}>
              {built || "—"}
            </GameTitle>
            <OptionsGrid>
              {letterButtons.map((btn) => (
                <OptionBtn
                  key={btn.key}
                  type="button"
                  $state={btn.used ? "disabled" : "idle"}
                  disabled={btn.used || checked}
                  onClick={() => setPicked((p) => [...p, btn.ch])}
                >
                  {btn.ch}
                </OptionBtn>
              ))}
            </OptionsGrid>
            <Md3Button
              title="Apagar"
              variant="text"
              onClick={() => setPicked([])}
              disabled={checked || picked.length === 0}
            />
            {!checked ? (
              <Md3Button
                title="Verificar"
                onClick={handleCheck}
                disabled={picked.length === 0}
              />
            ) : (
              <>
                <Feedback $ok={isCorrect}>
                  {isCorrect ? "Correto!" : `Resposta: ${current.answer}`}
                </Feedback>
                <Md3Button title="Próxima" onClick={handleNext} />
              </>
            )}
          </>
        )}
      </GamePageRoot>
    </MobileLayout>
  );
};

export default DactylologyPuzzleGame;
